/**
 * URL query parsing helpers.
 *
 * Goals:
 * - Order-independent parsing (URLSearchParams).
 * - Sanitize common malformed cases like multiple "?" inside search:
 *   "/x?page=1?tkTune=1" -> "?page=1&tkTune=1"
 * - Keep behavior conservative: only normalize when we detect malformed search.
 */
export const normalizeQueryString = (search: string): string => {
  if (!search) return '';

  const raw = String(search).trim();
  if (!raw) return '';

  // Ensure we always work with a leading "?"
  const withQuestion = raw.startsWith('?') ? raw : `?${raw}`;

  // Replace any additional "?" after the first with "&" (common paste / concat mistake).
  const first = withQuestion.indexOf('?');
  const head = withQuestion.slice(0, first + 1);
  const tail = withQuestion.slice(first + 1).replace(/\?/g, '&');

  // Clean up obvious separators: "?&" -> "?", "&&" -> "&"
  const cleaned = (head + tail)
    .replace(/\?&+/, '?')
    .replace(/&{2,}/g, '&')
    .replace(/&+$/, '');

  // Avoid returning a bare "?"
  return cleaned === '?' ? '' : cleaned;
};

export const getQueryParams = (search: string): URLSearchParams => {
  const normalized = normalizeQueryString(search);
  return new URLSearchParams(normalized);
};

